// Core
import React, { useState, useRef, useEffect } from "react"
import { isMobile } from "react-device-detect"
import Hammer from "react-hammerjs"

// Components
import ScrollSection from "../locomotiveScroll/scrollSection"
import JsonText from "../_jsonText"

// Imagery
import Arrow from "./images/arrow.inline.svg"
import SmallArrow from "./images/small-arrow.inline.svg"

// Style
import styles from "./imageGallery.module.scss"

const ImageGallery = ({
  count,
  scrollEvent,
  contentful_id,
  menuTitle,
  imageGalleryItems,
}) => {
  const [windowHalf, setWindowHalf] = useState(0),
    [activeItem, setActiveItem] = useState(0)

  const anchorOverride = menuTitle
    ? menuTitle.replace(/\s+/g, "-").toLowerCase()
    : null

  const gallery = useRef(),
    wrap = useRef(),
    cursor = useRef(),
    counter = useRef(),
    mobileCounter = useRef(),
    items = useRef([]),
    captionList = useRef(),
    captions = useRef([]),
    pos = useRef({
      x: null,
      y: null,
    }),
    targetPos = useRef({
      x: null,
      y: null,
    })

  let cursorDirection = 0

  let maxItem = imageGalleryItems.length - 1

  let renderRaf = null,
    stopTimeout = null

  const updateWindowSize = () => {
    setWindowHalf(window.innerWidth / 2)
    updateCaptionListHeight()
  }

  const trackCursor = e => {
    pos.current.x = e.clientX
    pos.current.y = e.clientY - wrap.current.getBoundingClientRect().top
    cursor.current.classList.add(styles.visible)

    if (pos.current.x > windowHalf && cursorDirection === 0) {
      cursorDirection = 1
      cursor.current.classList.add(styles.next)
    } else if (pos.current.x <= windowHalf && cursorDirection === 1) {
      cursorDirection = 0
      cursor.current.classList.remove(styles.next)
    }
  }

  const scheduleRender = () => {
    if (stopTimeout) {
      clearTimeout(stopTimeout)
    }
    if (renderRaf) return
    renderRaf = window.requestAnimationFrame(render)
  }

  const endRender = () => {
    if (cursor.current) cursor.current.classList.remove(styles.visible)
    stopTimeout = setTimeout(stop, 500)
  }

  const render = () => {
    if (!cursor.current) return
    if (targetPos.current.x && targetPos.current.y) {
      targetPos.current.x += (pos.current.x - targetPos.current.x) * 0.175
      targetPos.current.y += (pos.current.y - targetPos.current.y) * 0.175
    } else {
      pos.current.x = targetPos.current.x = window.innerWidth * 0.9
      pos.current.y = targetPos.current.y = 100
    }
    if (cursor.current)
      cursor.current.style.transform = `translateX(${targetPos.current.x}px) translateY(${targetPos.current.y}px)`
    renderRaf = window.requestAnimationFrame(render)
  }

  const stop = () => {
    if (renderRaf) window.cancelAnimationFrame(renderRaf)
    renderRaf = null
  }

  const clickChangeItem = e => {
    if (e.clientX > windowHalf) {
      next()
    } else {
      prev()
    }
  }

  const next = () => {
    removeCurrent()
    updateCurrent(activeItem + 1)
  }

  const prev = () => {
    removeCurrent()
    updateCurrent(activeItem - 1)
  }

  const removeCurrent = () => {
    if (items.current[activeItem] && captions.current[activeItem]) {
      items.current[activeItem].classList.add(styles.prev)
      items.current[activeItem].classList.remove(styles.active)
      captions.current[activeItem].classList.remove(styles.active)
    }
  }

  const updateCurrent = targetItem => {
    targetItem =
      targetItem > maxItem ? 0 : targetItem < 0 ? maxItem : targetItem

    if (gallery.current)
      gallery.current.setAttribute("data-current", targetItem)

    if (counter.current) counter.current.innerHTML = targetItem + 1
    if (mobileCounter.current) mobileCounter.current.innerHTML = targetItem + 1

    setActiveItem(targetItem)
  }

  const updateCaptionListHeight = () => {
    let tallest = 0

    if (!captions.current) return

    for (let x = 0; x < captions.current.length; x++) {
      let height = captions.current[x].getBoundingClientRect().height
      tallest = height > tallest ? height : tallest
    }

    captionList.current.style.minHeight = tallest + "px"
  }

  const addToItems = el => {
    if (el && !items.current.includes(el)) {
      items.current.push(el)
    }
  }

  const addToCaptions = el => {
    if (el && !captions.current.includes(el)) {
      captions.current.push(el)
    }
  }

  useEffect(() => {
    setWindowHalf(window.innerWidth / 2)
    window.addEventListener("resize", updateWindowSize)

    updateCaptionListHeight()

    setTimeout(() => {
      if (!isMobile && scrollEvent) {
        window.scroll.on("call", (value, way, obj) => {
          if (value === "gallery-" + contentful_id) scrollEvent(way, count, obj)
        })
      }
    }, 200)

    return () => {
      window.removeEventListener("resize", updateWindowSize)
      if (renderRaf) window.window.cancelAnimationFrame(renderRaf)
    }
  }, [])

  return (
    <ScrollSection>
      <div ref={gallery} data-current="0">
        <div
          id={`ep-${anchorOverride ? anchorOverride : "gallery"}`}
          className={styles.wrap}
          onClick={!isMobile ? clickChangeItem : null}
          onMouseEnter={scheduleRender}
          onMouseLeave={endRender}
          onMouseMove={trackCursor}
          ref={wrap}
          data-scroll
          data-scroll-call={`gallery-${contentful_id}`}
          data-scroll-offset="45%, 45%"
        >
          <div
            className={`${styles.cursor} ${styles.next} ${styles.visible}`}
            ref={cursor}
          >
            <div className={`${styles.cursorCounter} mono`}>
              <span ref={counter}>1</span>/{imageGalleryItems.length}
            </div>
            <div className={styles.cursorArrow}>
              <Arrow />
            </div>
          </div>
          <Hammer onSwipeLeft={next} onSwipeRight={prev}>
            <div className={styles.gallery}>
              {imageGalleryItems.map((el, x) => {
                return (
                  <picture
                    key={el.contentful_id}
                    className={x === 0 ? styles.active : null}
                    ref={addToItems}
                  >
                    <source
                      media="(min-width: 1024px)"
                      srcSet={el.desktopImage.fluid.src}
                    />
                    <img
                      src={
                        el.mobileImage
                          ? el.mobileImage.fluid.src
                          : el.desktopImageCropped.fluid.src
                      }
                      alt={el.desktopImage.description}
                    />
                  </picture>
                )
              })}
            </div>
          </Hammer>
        </div>
        <div className={`${styles.controls} flex flex--vert-align flex--push`}>
          <button onClick={prev}>
            <SmallArrow />
          </button>
          <div className={`${styles.counter} mono`}>
            <span ref={mobileCounter}>1</span>/{imageGalleryItems.length}
          </div>
          <button onClick={next}>
            <SmallArrow />
          </button>
        </div>
        <div className={styles.captions}>
          <ul ref={captionList}>
            {imageGalleryItems.map((el, x) => {
              return (
                <li
                  className={`${x === 0 ? styles.active : ""} mono`}
                  key={`${el.contentful_id}-caption`}
                  ref={addToCaptions}
                >
                  <JsonText {...el.caption} />
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </ScrollSection>
  )
}

export default ImageGallery
